import { AddCircleOutline, WaterDropOutlined, Opacity } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { Context, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IMainComponentType, MainComponentStatus } from 'src/enums'
import { permissionValue } from 'src/enums/permissionValue'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { IImpregnation } from 'src/generated-types/impregnation'
import { IMainComponent } from 'src/generated-types/main-component'
import { WashingReportDto } from 'src/generated-types/washing-report-dto'
import { useDialogCtx } from 'src/hooks/context-hooks'
import ImpregnationFormDialog from 'src/screens/customer/mainComponents/ImpregnationFormDialog'
import { WashingReportFormDialog } from 'src/screens/customer/mainComponents/WashingReportFormDialog'
import ImpregnationInEquipment from './ImpregnationInEquipment'
import WashingReportsInEquipment from './WashingReportsInEquipment'

import EquipmentSectionLayout from './EquipmentSectionLayout'

interface Props {
  context: Context<any> | null | undefined
  mainComponentId: any
  mainComponent: IMainComponent | undefined
  serviceStationId: EntityId | undefined
  isReadOnly?: boolean
}

const WashingReportAndImpregnationSection = (props: Props) => {
  const { context, mainComponent, mainComponentId, serviceStationId, isReadOnly } = props
  const { showDialog } = useDialogCtx()
  const { t } = useTranslation(['main-component', 'equipment', 'impregnation'])
  const theme = useTheme()
  const [leftCount, setLeftCount] = useState(0)
  const [rightCount, setRightCount] = useState(0)

  const isNet = mainComponent?.type?.componentType === IMainComponentType.Net

  const onAddImpregnation = (initialValues: IImpregnation | undefined) => {
    showDialog(
      ImpregnationFormDialog,
      {
        componentProps: {
          mainComponentId: mainComponentId as EntityId,
          serviceStationId: serviceStationId as EntityId,
          initialData: initialValues,
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        isAutoCloseDisabled: true,
      },
      undefined,
      true,
    )
  }

  const onAddWashingReport = (initialValues: WashingReportDto | undefined) => {
    showDialog(
      WashingReportFormDialog,
      {
        componentProps: {
          customerId: mainComponent?.customerId as EntityId,
          mainComponentId: mainComponentId as EntityId,
          initialData: initialValues,
          positionId: mainComponent?.positionId,
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        isAutoCloseDisabled: true,
      },
      undefined,
      true,
    )
  }

  const canAddImpregnation = mainComponent?.status === MainComponentStatus.InService

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: 'wrap' }}>
      <Box sx={{ flex: isNet ? '1 1 calc(50% - 8px)' : '1 1 100%', minWidth: 300 }}>
        <EquipmentSectionLayout
          title={t('washing-reports', { ns: 'equipment' })}
          icon={
            <WaterDropOutlined sx={{ fontSize: '18px', color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />
          }
          buttonText={t('washing-report', { ns: 'equipment' })}
          buttonIcon={<AddCircleOutline />}
          onButtonClick={() => onAddWashingReport(undefined)}
          context={context}
          permissions={[
            permissionValue.Locality.MainComponent.Document.Write,
            permissionValue.ClientAdminPermission,
            permissionValue.ServiceStation.Write,
            permissionValue.ClientAdminPortal.Equipment_Portal,
          ]}
          content={
            mainComponent && (
              <WashingReportsInEquipment
                onAddWashingReport={onAddWashingReport}
                context={context}
                mainComponentId={mainComponentId}
                mainComponent={mainComponent}
                positionId={mainComponent.positionId}
                height="200px"
                autoHeight={true}
                maxVisibleRowCount={5}
                minimalEmptyStatus={!isNet || rightCount < 4}
                setDataLength={setLeftCount}
                disabled={isReadOnly}
              />
            )
          }
          isReadOnly={isReadOnly}
        />
      </Box>

      {isNet && (
        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 300 }}>
          <EquipmentSectionLayout
            title={t('impregnations', { ns: 'impregnation' })}
            icon={<Opacity sx={{ fontSize: '18px', color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />}
            buttonText={canAddImpregnation ? t('impregnation', { ns: 'impregnation' }) : undefined}
            buttonIcon={canAddImpregnation ? <AddCircleOutline /> : undefined}
            onButtonClick={canAddImpregnation ? () => onAddImpregnation(undefined) : undefined}
            context={context}
            permissions={[
              permissionValue.Locality.MainComponent.Document.Write,
              permissionValue.ClientAdminPermission,
              permissionValue.ServiceStation.Write,
              permissionValue.ClientAdminPortal.Equipment_Portal,
            ]}
            content={
              mainComponent && (
                <ImpregnationInEquipment
                  onAddImpregnation={onAddImpregnation}
                  context={context}
                  mainComponentId={mainComponentId}
                  mainComponent={mainComponent}
                  serviceStationId={serviceStationId}
                  height="200px"
                  autoHeight={true}
                  maxVisibleRowCount={5}
                  minimalEmptyStatus={leftCount < 4}
                  setDataLength={setRightCount}
                  disabled={isReadOnly}
                />
              )
            }
            isReadOnly={isReadOnly}
          />
        </Box>
      )}
    </Box>
  )
}

export default WashingReportAndImpregnationSection
